import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { scrollToElementByDataId } from "../util/scrollToElement";
import { DEFAULT_THEME_VARIANT } from "../viewer/constants";
import {
  ViewerExpressReactComponent,
  type ViewerExpressPageData,
  type ViewerExpressReactComponentHandle,
  type ViewerExpressReactComponentProps,
} from "../viewer/components/ViewerExpressReactComponent";
import type { IAnnotation } from "@bronte/viewer/src/main";
import type { ScreenConfig, SpotlightApplyOptions } from "../types";

export type ViewerExpressLegacyWrapperHandlers = {
  route: (screenConfig: ScreenConfig, elementId?: string, dataId?: string) => void;
  setThemeVariant: (theme: any) => void;
  applySpotlight: (options: SpotlightApplyOptions) => void;
  clearSpotlight: () => void;
  addAnnotation: (annotation: IAnnotation) => void;
  destroyPageObserver: () => void;
  print: () => void;
};

export type ViewerExpressLegacyWrapperProps = Omit<
  ViewerExpressReactComponentProps,
  "page" | "themeVariant" | "onComponentReRendered"
> & {
  onScreenChange?: (elementId: string) => void;
  onScreenRendered?: (elementId: string) => void;
  initialPage?: ViewerExpressPageData | null;
  initialThemeVariant?: any;
};

export const ViewerExpressLegacyWrapper = forwardRef<
  ViewerExpressLegacyWrapperHandlers,
  ViewerExpressLegacyWrapperProps
>(function ViewerExpressLegacyWrapper(
  {
    onScreenChange,
    onScreenRendered,
    initialPage = null,
    initialThemeVariant = DEFAULT_THEME_VARIANT,
    ...componentProps
  },
  ref,
) {
  const componentRef = useRef<ViewerExpressReactComponentHandle>(null);
  const [currentPage, setCurrentPage] = useState<ViewerExpressPageData | null>(initialPage);
  const [currentThemeVariant, setCurrentThemeVariant] = useState<any>(initialThemeVariant);
  const currentPageRef = useRef<ViewerExpressPageData | null>(currentPage);
  currentPageRef.current = currentPage;
  const previousElementIdRef = useRef<string | undefined>(undefined);

  // Remember the first screen id so we do not notify the host twice for the same screen.
  useLayoutEffect(() => {
    if (initialPage?.elementId) {
      previousElementIdRef.current = initialPage.elementId;
    }
  }, [initialPage?.elementId]);

  const route = useCallback(
    (screenConfig: ScreenConfig, elementId?: string, dataId?: string) => {
      if (!screenConfig) {
        console.error("Screen config is required");
        return;
      }

      componentRef.current?.clearSpotlight();

      const existingPage = currentPageRef.current;
      if (elementId && existingPage?.elementId === elementId && existingPage !== null) {
        if (dataId) {
          requestAnimationFrame(() => {
            scrollToElementByDataId(dataId);
            componentRef.current?.notifyPageByDataId(dataId);
          });
        }
        return;
      }

      if (elementId && previousElementIdRef.current !== elementId) {
        onScreenChange?.(elementId);
        previousElementIdRef.current = elementId;
      }

      setCurrentPage({ screenConfig, elementId, dataId });
    },
    [onScreenChange],
  );

  const setThemeVariant = useCallback((theme: any) => {
    setCurrentThemeVariant(theme);
  }, []);

  const applySpotlight = useCallback((options: SpotlightApplyOptions) => {
    componentRef.current?.applySpotlight(options);
  }, []);

  const clearSpotlight = useCallback(() => {
    componentRef.current?.clearSpotlight();
  }, []);

  const addAnnotation = useCallback((annotation: IAnnotation) => {
    componentRef.current?.addAnnotation(annotation);
  }, []);

  const destroyPageObserver = useCallback(() => {
    componentRef.current?.destroyPageObserver();
  }, []);

  const print = useCallback(() => {
    componentRef.current?.print();
  }, []);

  const handleComponentReRendered = useCallback(
    (elementId?: string) => {
      const resolvedElementId = elementId ?? currentPageRef.current?.elementId;
      if (resolvedElementId) {
        onScreenRendered?.(resolvedElementId);
      }
    },
    [onScreenRendered],
  );

  useImperativeHandle(
    ref,
    () => ({
      route,
      setThemeVariant,
      applySpotlight,
      clearSpotlight,
      addAnnotation,
      destroyPageObserver,
      print,
    }),
    [
      route,
      setThemeVariant,
      applySpotlight,
      clearSpotlight,
      addAnnotation,
      destroyPageObserver,
      print,
    ],
  );

  return (
    <ViewerExpressReactComponent
      ref={componentRef}
      {...componentProps}
      onComponentReRendered={handleComponentReRendered}
      page={currentPage}
      themeVariant={currentThemeVariant}
    />
  );
});
